import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { createInquiry, parseInquiryError, REASON_CODE_LABELS } from "@/lib/modulD";
import RichTextView from "@/components/common/RichTextView";
import RichTextEditor from "@/components/common/RichTextEditor";
import { useCatalogLabels } from "@/i18n/catalog";
import SellerRatingBadge from "@/components/reviews/SellerRatingBadge";
import ReviewsList from "@/components/reviews/ReviewsList";
import NegotiationSheet from "@/components/marketplace/NegotiationSheet";
import ScarcityNudge from "@/components/marketplace/ScarcityNudge";
import { ArrowLeft, Heart, MessageCircle, ShoppingBag, ShoppingCart, Truck, AlertCircle, Info, Clock, HandCoins } from "lucide-react";

export default function MarketplaceDetailPage() {
  const { t } = useTranslation();
  const cat = useCatalogLabels();
  const { listingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgIdx, setImgIdx] = useState(0);
  const [showGuestCheckout, setShowGuestCheckout] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showInquiry, setShowInquiry] = useState(false);
  const [inquiryText, setInquiryText] = useState("");
  const [inquirySending, setInquirySending] = useState(false);
  const [inquiryDone, setInquiryDone] = useState(false);
  const [inquiryConvId, setInquiryConvId] = useState(null);
  const [inquiryBlock, setInquiryBlock] = useState(null);
  const [inquiryWarning, setInquiryWarning] = useState(null);
  const [guestEmail, setGuestEmail] = useState("");
  const [guestName, setGuestName] = useState("");
  const [guestAddress, setGuestAddress] = useState("");
  const [guestCity, setGuestCity] = useState("");
  const [guestZip, setGuestZip] = useState("");
  const [shipName, setShipName] = useState("");
  const [shipAddress, setShipAddress] = useState("");
  const [shipCity, setShipCity] = useState("");
  const [shipZip, setShipZip] = useState("");
  const [purchasing, setPurchasing] = useState(false);
  const [orderDone, setOrderDone] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);
  const [showNegotiation, setShowNegotiation] = useState(false);
  const [sellerMore, setSellerMore] = useState([]);

  const handleAddToCart = async () => {
    if (!user || user === false) { navigate("/login?next=/marketplace/" + listingId); return; }
    setAddingToCart(true);
    try {
      await api.post("/cart/items", { listing_id: listingId, quantity: 1 });
      window.dispatchEvent(new Event("cart-refresh"));
      setAddingToCart("done");
      setTimeout(() => setAddingToCart(false), 1400);
    } catch (e) {
      alert(e.response?.data?.detail || "Zum Warenkorb hinzufügen fehlgeschlagen");
      setAddingToCart(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/listings/${listingId}`);
        setListing(data);
      } catch { setListing(null); }
      finally { setLoading(false); }
    })();
  }, [listingId]);

  // iter119: "Mehr vom Verkäufer" — nur laden, wenn seller.handle bekannt.
  useEffect(() => {
    if (!listing?.seller?.handle) return;
    (async () => {
      try {
        const { data } = await api.get(`/profiles/${listing.seller.handle}/listings?area=MARKETPLACE&limit=6`);
        const others = (data?.listings || []).filter((l) => l.listing_id !== listingId);
        setSellerMore(others.slice(0, 4));
      } catch { setSellerMore([]); }
    })();
  }, [listing?.seller?.handle, listingId]);

  const toggleWatch = async () => {
    if (!user || user === false) { navigate("/login"); return; }
    try {
      const { data } = await api.post(`/listings/${listingId}/watch`);
      setListing(prev => ({ ...prev, is_watched: data.watched, watch_count: data.watched ? (prev.watch_count || 0) + 1 : Math.max(0, (prev.watch_count || 0) - 1) }));
    } catch { /* ignore */ }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;
  if (!listing) return <div className="px-4 py-16 text-center text-sm text-[#999]">{t("marketplace_detail_extra.item_not_found")}</div>;

  const handleGuestCheckout = async () => {
    if (!guestEmail || !guestName || !guestAddress || !guestCity || !guestZip) return;
    setPurchasing(true);
    try {
      const { data } = await api.post("/orders/guest-checkout", {
        listing_id: listingId, email: guestEmail, guest_name: guestName,
        shipping_address: guestAddress, shipping_city: guestCity, shipping_zip: guestZip,
      });
      setOrderDone(data);
    } catch { /* ignore */ }
    finally { setPurchasing(false); }
  };

  const handleLoggedInCheckout = async () => {
    if (!shipName || !shipAddress || !shipCity || !shipZip) return;
    setPurchasing(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        listing_id: listingId, shipping_name: shipName,
        shipping_address: shipAddress, shipping_city: shipCity, shipping_zip: shipZip,
      });
      setOrderDone(data);
    } catch { /* ignore */ }
    finally { setPurchasing(false); }
  };

  if (orderDone) {
    return (
      <div data-testid="mp-order-done" className="flex flex-col items-center justify-center px-6 py-16">
        <ShoppingBag size={48} className="text-green-500 mb-4" />
        <h2 className="text-lg font-bold text-[#0A0A0A]">{t("mp_detail.order_placed")}</h2>
        <p className="text-sm text-[#666] mt-2 text-center">{t("mp_detail.order_number")} <span className="font-mono text-xs">{orderDone.order_id}</span></p>
        <button onClick={() => navigate("/marketplace")} className="mt-6 h-10 px-6 rounded-full bg-[#0A0A0A] text-white text-sm font-medium">{t("mp_detail.continue_browsing")}</button>
      </div>
    );
  }

  const seller = listing.seller || {};
  const images = listing.media_urls || [];

  return (
    <div data-testid="mp-detail-page" className="pb-24 w-full max-w-3xl md:max-w-6xl 2xl:max-w-full mx-auto 2xl:mx-0">
      {/* Top Bar */}
      <div className="sticky top-0 z-40 flex items-center gap-3 px-4 py-2 bg-white border-b border-[#E5E5E5]">
        <button data-testid="mp-detail-back" onClick={() => navigate(-1)} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <span className="text-xs text-[#999] flex-1">{listing.public_item_no}</span>
        <button data-testid="mp-detail-watch" onClick={toggleWatch} className="p-1">
          <Heart size={20} className={listing.is_watched ? "fill-[#0A0A0A] text-[#0A0A0A]" : "text-[#666]"} />
        </button>
      </div>

      {/* iter118: 2-Column-Split ab md — Gallery links, Info+Buttons rechts */}
      <div className="md:grid md:grid-cols-2 md:gap-6 md:px-4 md:pt-4 md:items-start">

      {/* iter172k · Sticky-Wrapper (analog STX): garantiert Slide-Room innerhalb der Row-Höhe. */}
      <div className="md:sticky md:top-4 md:self-start md:h-fit">

      {/* Gallery */}
      <div className="relative aspect-[4/5] bg-[#F5F5F5] mx-3 mt-3 md:mx-0 md:mt-0 rounded-2xl overflow-hidden">
        {images.length > 0 ? (
          <img src={images[imgIdx]} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#CCC]">{t("mp_detail.no_image")}</div>
        )}
        {images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
            {images.map((_, i) => (
              <button key={i} onClick={() => setImgIdx(i)} className={`w-1.5 h-1.5 rounded-full ${i === imgIdx ? "bg-white" : "bg-white/40"}`} />
            ))}
          </div>
        )}
      </div>

      {/* iter172k · Ende Sticky-Wrapper */}
      </div>

      {/* Info-Spalte (rechts ab md) */}
      <div className="md:min-w-0">

      {/* Info */}
      <div className="px-4 py-4 md:px-0 md:pt-0 border-b border-[#E5E5E5]">
        <h1 className="text-lg font-bold text-[#0A0A0A]">{listing.title}</h1>
        <p className="text-2xl font-bold text-[#0A0A0A] mt-1">{listing.price?.toFixed(2)} {listing.currency}</p>
        {/* iter174n-followup7 · Immer i18n-lokalisierter Zustand.
             Fallback-Kette: catalog-i18n(condition_code) → backend-DE-Label → raw code.
             Verhindert Anzeigen wie „Zustand: CLEAN_REQUIRED". */}
        {listing.condition_code && (
          <span data-testid="mp-condition" className="inline-block mt-1 px-3 py-0.5 rounded-full bg-[#F5F5F5] border border-[#E5E5E5] text-xs text-[#666]">
            {t("mp_detail.condition_label")} {cat.condition(listing.condition_code, listing.condition_label)}
          </span>
        )}

        {/* iter80.9 Batch 6 Sub-c: strukturierte Attribut-Tabelle */}
        {listing.area_scope === "MARKETPLACE" && (listing.size || listing.color || listing.material || listing.subcategory_code) && (
          <div data-testid="mp-detail-attributes" className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 border border-[#E5E5E5] p-3 text-[11px]">
            {/* iter174n-followup7 · Subcategory/Color/Material immer i18n-lokalisiert. */}
            {listing.subcategory_code && (
              <><span className="text-[#999]">{t("mp_detail.attr_subcategory")}</span><span className="text-[#0A0A0A]">{cat.mpSub(listing.category_code, listing.subcategory_code, listing.subcategory_label)}</span></>
            )}
            {listing.size && (
              <><span className="text-[#999]">{t("mp_detail.attr_size")}</span><span className="text-[#0A0A0A]">{listing.size}</span></>
            )}
            {listing.color && (
              <><span className="text-[#999]">{t("mp_detail.attr_color")}</span><span className="text-[#0A0A0A]">{cat.color(listing.color)}</span></>
            )}
            {listing.material && (
              <><span className="text-[#999]">{t("mp_detail.attr_material")}</span><span className="text-[#0A0A0A]">{cat.material(listing.material)}</span></>
            )}
          </div>
        )}
        {/* iter79 Batch 2: Preisvorschlag nur bei Festpreis + Fremder Verkäufer */}
        {listing.sale_mode === "FIXED_PRICE" && listing.seller_profile_id !== user?._id && (
          <button
            data-testid="mp-detail-negotiate"
            onClick={() => setShowNegotiation(true)}
            className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#0A0A0A] underline hover:no-underline">
            <HandCoins size={12} /> {t("mp_detail.propose_price")}
          </button>
        )}
      </div>

      {/* Shipping */}
      <div className="px-4 py-3 border-b border-[#E5E5E5] space-y-1.5">
        <div className="flex items-center gap-3">
          <Truck size={16} className="text-[#999]" />
          <p className="text-xs text-[#666]">
            {listing.shipping_mode === "ANONYMOUS_HUB" ? t("mp_detail.ship_anonymous") : t("mp_detail.ship_direct")}
          </p>
        </div>
        {Number(listing.effective_processing_delay_days) > 0 && (
          <div data-testid="mp-detail-processing-delay" className="flex items-center gap-3">
            <Clock size={16} className="text-amber-500" />
            <p className="text-xs text-[#666]">
              <span className="font-medium">{t("mp_detail.extra_processing")}</span> {listing.effective_processing_delay_days} {listing.effective_processing_delay_days === 1 ? t("mp_detail.day_one") : t("mp_detail.day_other")}
            </p>
          </div>
        )}
      </div>

      {/* Seller */}
      <button
        data-testid="mp-detail-seller"
        onClick={() => seller.handle && navigate(`/user/${seller.handle}/marketplace`)}
        className="w-full flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5] hover:bg-[#FAFAFA] transition-colors"
      >
        {seller.avatar_url ? (
          <img src={seller.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-[#E5E5E5] flex items-center justify-center text-sm font-bold text-[#999]">
            {(seller.display_name || "?")[0]}
          </div>
        )}
        <div className="text-left">
          <p className="text-sm font-medium text-[#0A0A0A]">{seller.display_name}</p>
          {listing.seller_profile_id && (
            <SellerRatingBadge sellerId={listing.seller_profile_id} area="MP" />
          )}
        </div>
      </button>

      {/* iter172k · Description + Reviews wandern in die Info-Spalte, damit die
          rechte Spalte länger wird als der Sticky-Hero-Wrapper links → Sticky-Scroll-Room. */}
      {/* Description */}
      <div className="px-4 py-4 md:px-0 border-b border-[#E5E5E5]">
        <h3 className="text-xs font-bold text-[#999] uppercase tracking-wider mb-2">{t("mp_detail.description")}</h3>
        <RichTextView html={listing.description} className="text-sm text-[#666] leading-relaxed prose prose-sm max-w-none" />
      </div>

      {/* Reviews (iter88.21) */}
      <div className="px-4 py-4 md:px-0 border-b border-[#E5E5E5]" data-testid="mp-detail-reviews">
        <h3 className="text-xs font-bold text-[#999] uppercase tracking-wider mb-2">{t("mp_detail.reviews")}</h3>
        <ReviewsList scope="listing" scopeId={listing.listing_id} area="MP" limit={5} emptyText={t("mp_detail.no_review")} />
      </div>

      {/* Watch count */}
      <div className="px-4 py-3 md:px-0 text-xs text-[#999]">
        {t("mp_detail.watchers", { count: listing.watch_count || 0 })}
      </div>

      {/* /Info-Spalte — Core-Info endet hier. Der Rest fließt full-width unter der Gallery. */}
      </div>
      {/* /2-Column-Split */}
      </div>

      {/* iter172k · Restlicher Full-Width-Content (Checkout, Bottom-CTA, mehr vom Verkäufer) */}
      <div className="md:px-4 md:pt-4 md:max-w-6xl md:mx-auto 2xl:mx-0">

      {/* Logged-in Checkout Form */}
      {showCheckout && user && user !== false && (
        <div data-testid="mp-checkout-form" className="px-4 py-4 border-b border-[#E5E5E5] space-y-3">
          <p className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("mp_detail.shipping_data")}</p>
          <input data-testid="mp-ship-name" value={shipName} onChange={(e) => setShipName(e.target.value)}
            className="w-full h-10 px-4 rounded-full border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" placeholder={t("mp_detail.name_star")} />
          <input data-testid="mp-ship-address" value={shipAddress} onChange={(e) => setShipAddress(e.target.value)}
            className="w-full h-10 px-4 rounded-full border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" placeholder={t("mp_detail.address_star")} />
          <div className="flex gap-2">
            <input data-testid="mp-ship-zip" value={shipZip} onChange={(e) => setShipZip(e.target.value)}
              className="w-24 h-10 px-4 rounded-full border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" placeholder={t("mp_detail.zip_star")} />
            <input data-testid="mp-ship-city" value={shipCity} onChange={(e) => setShipCity(e.target.value)}
              className="flex-1 h-10 px-4 rounded-full border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" placeholder={t("mp_detail.city_star")} />
          </div>
          <button data-testid="mp-checkout-submit" onClick={handleLoggedInCheckout} disabled={purchasing || !shipName || !shipAddress}
            className="w-full h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-30">
            <ShoppingBag size={14} /> {purchasing ? t("mp_detail.ordering") : t("mp_detail.buy_now")}
          </button>
        </div>
      )}

      {/* Guest Checkout Form */}
      {showGuestCheckout && (
        <div data-testid="mp-guest-checkout" className="px-4 py-4 border-b border-[#E5E5E5] space-y-3">
          <p className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("mp_detail.guest_buy")}</p>
          <input data-testid="mp-guest-email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)}
            className="w-full h-10 px-4 rounded-full border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" placeholder={t("mp_detail.email_star")} />
          <input data-testid="mp-guest-name" value={guestName} onChange={(e) => setGuestName(e.target.value)}
            className="w-full h-10 px-4 rounded-full border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" placeholder={t("mp_detail.name_star")} />
          <input data-testid="mp-guest-address" value={guestAddress} onChange={(e) => setGuestAddress(e.target.value)}
            className="w-full h-10 px-4 rounded-full border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" placeholder={t("mp_detail.address_star")} />
          <div className="flex gap-2">
            <input data-testid="mp-guest-zip" value={guestZip} onChange={(e) => setGuestZip(e.target.value)}
              className="w-24 h-10 px-4 rounded-full border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" placeholder={t("mp_detail.zip_star")} />
            <input data-testid="mp-guest-city" value={guestCity} onChange={(e) => setGuestCity(e.target.value)}
              className="flex-1 h-10 px-4 rounded-full border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" placeholder={t("mp_detail.city_star")} />
          </div>
          <button data-testid="mp-guest-submit" onClick={handleGuestCheckout} disabled={purchasing || !guestEmail || !guestName || !guestAddress}
            className="w-full h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-30">
            <ShoppingBag size={14} /> {purchasing ? t("mp_detail.ordering") : t("mp_detail.order_now")}
          </button>
        </div>
      )}

      {/* Inquiry Form */}
      {showInquiry && (
        <div data-testid="mp-inquiry-form" className="px-4 py-4 border-b border-[#E5E5E5] space-y-3">
          <p className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("mp_detail.question_title")}</p>
          {inquiryDone ? (
            <div className="space-y-2">
              <InfoCard variant="success">
                {t("mp_detail.question_sent")}
              </InfoCard>
              {inquiryWarning && (
                <InfoCard variant="warning" data-testid="mp-inquiry-redact-warning">
                  <p className="text-[11px] text-amber-900 leading-relaxed">{inquiryWarning}</p>
                  <button onClick={() => setInquiryWarning(null)} className="mt-1 text-[10px] text-amber-700 underline hover:text-amber-900">
                    {t("mp_detail.understood")}
                  </button>
                </InfoCard>
              )}
              {inquiryConvId && (
                <button data-testid="mp-inquiry-goto-thread"
                  onClick={() => navigate(`/messages/inquiry/${inquiryConvId}`)}
                  className="w-full h-9 rounded-full border border-[#0A0A0A] text-[#0A0A0A] text-xs font-bold">
                  {t("mp_detail.goto_thread")}
                </button>
              )}
            </div>
          ) : (
            <>
              {inquiryBlock && (
                <InfoCard variant="danger" testId="mp-inquiry-block-banner">
                  <AlertCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-bold text-red-700">{REASON_CODE_LABELS[inquiryBlock.reason_code] || t("mp_detail.msg_blocked")}</p>
                    <p className="text-xs text-red-700 mt-0.5">{inquiryBlock.reason_text}</p>
                  </div>
                </InfoCard>
              )}
              <div data-testid="mp-inquiry-text-wrap">
                <RichTextEditor
                  value={inquiryText}
                  onChange={(html) => { setInquiryText(html); if (inquiryBlock) setInquiryBlock(null); }}
                  placeholder={t("mp_detail.question_placeholder")}
                  minHeight="90px"
                  testId="mp-inquiry-text-rt"
                />
              </div>
              <div className="flex items-start gap-1.5 px-1">
                <Info size={10} className="text-[#999] mt-0.5 shrink-0" />
                <p className="text-[10px] text-[#999]">{t("mp_detail.policy_hint")}</p>
              </div>
              <button data-testid="mp-inquiry-send" onClick={async () => {
                const plain = (inquiryText || "")
                  .replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
                  .replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ")
                  .replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">")
                  .replace(/\n{3,}/g, "\n\n").trim();
                if (!plain) return;
                setInquirySending(true);
                setInquiryBlock(null);
                setInquiryWarning(null);
                try {
                  const res = await createInquiry(listingId, plain);
                  setInquiryConvId(res.conversation_id);
                  // Detect REDACTED moderation result and inform sender
                  const mod = res?.message?.moderation;
                  if (mod && (mod.decision === "REDACTED" || mod.redaction_count > 0)) {
                    const c = mod.redaction_count;
                    setInquiryWarning(
                      `${t("mp_detail.redact_prefix")} ${c || ""} ${c === 1 ? t("mp_detail.redact_contact_one") : t("mp_detail.redact_contact_other")} ${t("mp_detail.redact_suffix")}`
                    );
                  }
                  setInquiryDone(true);
                  setInquiryText("");
                } catch (err) {
                  const parsed = parseInquiryError(err);
                  if (parsed.blocked) {
                    setInquiryBlock(parsed);
                  } else if (parsed.feature_disabled) {
                    setInquiryBlock({ reason_code: "FEATURE_DISABLED", reason_text: t("mp_detail.feature_disabled") });
                  } else {
                    setInquiryBlock({ reason_code: "ERROR", reason_text: parsed.message });
                  }
                }
                finally { setInquirySending(false); }
              }} disabled={inquirySending || !(inquiryText || "").replace(/<[^>]+>/g, "").trim()}
                className="w-full h-10 rounded-full bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-30">
                <MessageCircle size={14} /> {inquirySending ? t("mp_detail.sending") : t("mp_detail.send_question")}
              </button>
            </>
          )}
        </div>
      )}

      {/* Bottom CTA */}

      {/* iter119 (Message 959): Mehr vom Verkäufer — full-width unter Beschreibung. */}
      {sellerMore.length > 0 && (
        <section data-testid="mp-detail-more-from-seller" className="px-4 py-6 md:px-0 border-t border-[#E5E5E5]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-[#999] uppercase tracking-wider">{t("mp_detail.more_from_seller", { defaultValue: "Mehr vom Verkäufer" })}</h3>
            {seller.handle && (
              <button
                data-testid="mp-detail-more-from-seller-all"
                onClick={() => navigate(`/user/${seller.handle}/marketplace`)}
                className="text-xs text-[#0A0A0A] underline hover:no-underline"
              >
                {t("mp_detail.view_all", { defaultValue: "Alle ansehen" })}
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {sellerMore.map((l) => (
              <button
                key={l.listing_id}
                data-testid={`mp-more-item-${l.listing_id}`}
                onClick={() => navigate(`/marketplace/${l.listing_id}`)}
                className="block text-left group"
              >
                <div className="aspect-square rounded-xl overflow-hidden bg-[#F5F5F5]">
                  {l.media_urls?.[0] && (
                    <img src={l.media_urls[0]} alt={l.title || ""} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  )}
                </div>
                <p className="mt-1.5 text-xs font-medium text-[#0A0A0A] line-clamp-1">{l.title}</p>
                <p className="text-[11px] text-[#666]">{l.price?.toFixed(2)} {l.currency}</p>
              </button>
            ))}
          </div>
        </section>
      )}
      </div>
      {/* /Full-Width-Content */}

      {/* iter140 · Scarcity-Nudge (nur bei Unikaten) */}
      <div className="max-w-3xl mx-auto px-4 md:px-6 mt-3">
        <ScarcityNudge listingId={listing.listing_id} />
      </div>

      {/* Action-Bar: fixed am Handy, inline unter Info ab md */}
      <div className="fixed bottom-16 left-0 right-0 max-w-md mx-auto bg-white border-t border-[#E5E5E5] px-4 py-3 flex gap-2 z-40 md:static md:max-w-3xl md:mt-4 md:border md:rounded-2xl md:shadow-sm 2xl:bottom-4">
        {listing.seller_profile_id && user && listing.seller_profile_id === user._id ? (
          <div data-testid="mp-detail-own-listing-notice" className="flex-1 h-11 rounded-full border border-[#E5E5E5] text-[#999] text-xs font-medium flex items-center justify-center px-3 text-center">
            {t("mp_detail.own_listing")}
          </div>
        ) : (
          <>
        {!showInquiry ? (
          <button
            data-testid="mp-detail-inquiry"
            onClick={() => {
              if (!user || user === false) { navigate("/login"); return; }
              setShowInquiry(true);
              setTimeout(() => {
                const ta = document.querySelector('[data-testid="mp-inquiry-text"]');
                if (ta) {
                  ta.scrollIntoView({ behavior: "smooth", block: "center" });
                  ta.focus();
                }
              }, 100);
            }}
            className="flex-1 h-11 rounded-full border border-[#0A0A0A] text-[#0A0A0A] text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-[#F5F5F5] transition-colors active:scale-[0.98]"
          >
            <MessageCircle size={14} /> {t("mp_detail.ask_question")}
          </button>
        ) : (
          <button
            data-testid="mp-detail-inquiry-cancel"
            onClick={() => { setShowInquiry(false); setInquiryBlock(null); setInquiryText(""); }}
            className="flex-1 h-11 rounded-full border border-[#E5E5E5] text-[#666] text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-[#F5F5F5] transition-colors active:scale-[0.98]"
          >
            {t("mp_detail.cancel")}
          </button>
        )}
        <button
          data-testid="mp-detail-add-cart"
          onClick={handleAddToCart}
          disabled={addingToCart === true}
          className="h-11 px-4 rounded-full border border-[#333] text-[#333] text-sm font-medium flex items-center justify-center gap-1.5 hover:border-[#0A0A0A] transition-colors active:scale-[0.98] disabled:opacity-60">
          <ShoppingCart size={14} /> {addingToCart === "done" ? t("mp_detail.cart_added") : t("mp_detail.cart")}
        </button>
        <button
          data-testid="mp-detail-buy"
          onClick={() => navigate(`/checkout?listing=${listingId}&area=MARKETPLACE`)}
          className="flex-1 h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-[#222] transition-colors active:scale-[0.98]"
        >
          <ShoppingBag size={14} /> {t("mp_detail.buy")}
        </button>
          </>
        )}
      </div>
      {/* iter79 Batch 2: Negotiation Sheet */}
      {showNegotiation && (
        <NegotiationSheet
          listing={listing}
          onClose={() => setShowNegotiation(false)}
          onNavigateToCheckout={(neg) => navigate(`/checkout?listing=${listingId}&area=MARKETPLACE&negotiation_id=${neg.negotiation_id}`)}
        />
      )}
    </div>
  );
}
